import { useState, useEffect } from "react";
import { Bell, Mail, Phone, Building2, User, Briefcase, Plane, Clock, CheckCircle, ChevronDown, ChevronUp, Circle } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new:       { label: "New",       color: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
  contacted: { label: "Contacted", color: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
  scheduled: { label: "Scheduled", color: "text-green-400 bg-green-500/10 border-green-500/30" },
  closed:    { label: "Closed",    color: "text-slate-500 bg-slate-800/40 border-slate-700/30" },
};

const STATUS_ORDER = ["new", "contacted", "scheduled", "closed"];

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)    return `${Math.round(diff)}s ago`;
  if (diff < 3600)  return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function DemoRequestCard({ req, onMarkRead, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const st = STATUS_CONFIG[req.status] || STATUS_CONFIG.new;

  const handleExpand = () => {
    setExpanded(!expanded);
    if (!req.read && !expanded) onMarkRead(req.id);
  };

  const handleStatus = async (newStatus) => {
    setStatusLoading(true);
    await onStatusChange(req.id, newStatus);
    setStatusLoading(false);
  };

  return (
    <div
      className={`border transition-all ${req.read ? "border-white/5 bg-[#07111f]" : "border-orange-500/20 bg-[#0c1929]/40"}`}
      data-testid={`demo-request-${req.id}`}>
      {/* Header row */}
      <button
        onClick={handleExpand}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/2 transition-colors"
        data-testid={`demo-request-expand-${req.id}`}>
        {/* Unread dot */}
        <div className={`w-2 h-2 rounded-full shrink-0 ${req.read ? "bg-transparent" : "bg-orange-400"}`} />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-bold text-white">{req.name}</span>
            <span className="text-xs text-slate-500">{req.airline}</span>
            {req.fleet_size && (
              <span className="text-[9px] text-slate-600 marketing-font-mono border border-white/5 px-2 py-0.5">{req.fleet_size}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-600">{req.role}</span>
            <span className="text-[10px] text-slate-700">{req.email}</span>
          </div>
        </div>

        {/* Status badge */}
        <span className={`text-[9px] font-bold marketing-font-mono uppercase px-2.5 py-1 border ${st.color} shrink-0`}>
          {st.label}
        </span>

        {/* Time */}
        <span className="text-[10px] text-slate-600 shrink-0 w-16 text-right">{timeAgo(req.created_at)}</span>

        {/* Expand */}
        {expanded ? <ChevronUp size={14} className="text-slate-600 shrink-0" /> : <ChevronDown size={14} className="text-slate-600 shrink-0" />}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-5 border-t border-white/5">
          <div className="grid sm:grid-cols-3 gap-4 mt-4 mb-4">
            <div className="flex items-center gap-2">
              <Mail size={12} className="text-slate-600" />
              <a href={`mailto:${req.email}`} className="text-xs text-orange-400 hover:text-orange-300 transition-colors">{req.email}</a>
            </div>
            {req.phone && (
              <div className="flex items-center gap-2">
                <Phone size={12} className="text-slate-600" />
                <span className="text-xs text-slate-400">{req.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Clock size={12} className="text-slate-600" />
              <span className="text-xs text-slate-500">{new Date(req.created_at).toLocaleString()}</span>
            </div>
          </div>

          {req.message && (
            <div className="bg-[#04090f] border border-white/5 px-4 py-3 mb-4">
              <div className="text-[9px] text-slate-600 marketing-font-mono uppercase tracking-widest mb-2">Message</div>
              <p className="text-sm text-slate-400 leading-relaxed">{req.message}</p>
            </div>
          )}

          {/* Status controls */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] text-slate-600 marketing-font-mono uppercase tracking-widest">Update status:</span>
            {STATUS_ORDER.map(s => {
              const sc = STATUS_CONFIG[s];
              return (
                <button
                  key={s}
                  disabled={req.status === s || statusLoading}
                  onClick={() => handleStatus(s)}
                  className={`text-[9px] font-bold marketing-font-mono uppercase px-3 py-1.5 border transition-all ${
                    req.status === s
                      ? sc.color + " opacity-100 cursor-default"
                      : "border-white/10 text-slate-500 hover:border-white/30 hover:text-white"
                  }`}
                  data-testid={`status-btn-${s}-${req.id}`}>
                  {sc.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DemoRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const token = localStorage.getItem("fs_token");

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_BASE}/demo-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleMarkRead = async (id) => {
    await fetch(`${API_BASE}/demo-requests/${id}/read`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    setRequests(prev => prev.map(r => r.id === id ? { ...r, read: true } : r));
  };

  const handleStatusChange = async (id, newStatus) => {
    await fetch(`${API_BASE}/demo-requests/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: newStatus }),
    });
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
  };

  const filtered = filter === "all" ? requests : requests.filter(r => r.status === filter);
  const unreadCount = requests.filter(r => !r.read).length;

  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = requests.filter(r => r.status === s).length;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-black text-foreground tracking-tight">Demo Requests</h1>
            {unreadCount > 0 && (
              <span className="text-[10px] font-black px-2 py-1 bg-orange-500 text-white rounded-sm" data-testid="unread-badge">
                {unreadCount} new
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">Incoming demo requests from the GroundShield marketing site.</p>
        </div>
        <a href="/marketing/demo" target="_blank" rel="noreferrer"
          className="text-xs text-muted-foreground hover:text-foreground border border-border px-4 py-2 transition-colors">
          View form live
        </a>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total", value: requests.length, color: "text-foreground" },
          { label: "Unread", value: unreadCount, color: "text-orange-400" },
          { label: "Scheduled", value: counts.scheduled || 0, color: "text-green-400" },
          { label: "Closed", value: counts.closed || 0, color: "text-muted-foreground" },
        ].map((s, i) => (
          <div key={i} className="bg-secondary/20 border border-border p-4 rounded-sm">
            <div className={`text-2xl font-black font-mono ${s.color} mb-1`}>{s.value}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 border-b border-border">
        {[
          { key: "all", label: `All (${requests.length})` },
          { key: "new", label: `New (${counts.new || 0})` },
          { key: "contacted", label: `Contacted (${counts.contacted || 0})` },
          { key: "scheduled", label: `Scheduled (${counts.scheduled || 0})` },
          { key: "closed", label: `Closed (${counts.closed || 0})` },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              filter === f.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`filter-tab-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-muted-foreground text-center py-16">Loading requests...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Bell size={32} className="text-muted-foreground/30 mx-auto mb-3" />
          <div className="text-sm text-muted-foreground">
            {filter === "all" ? "No demo requests yet. Share the marketing site!" : `No ${filter} requests.`}
          </div>
          <a href="/marketing" target="_blank" rel="noreferrer"
            className="mt-4 inline-block text-xs text-primary hover:text-primary/80 transition-colors">
            Open marketing site
          </a>
        </div>
      ) : (
        <div className="space-y-1">
          {filtered.map(req => (
            <DemoRequestCard
              key={req.id}
              req={req}
              onMarkRead={handleMarkRead}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}
